import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../context/AuthContext'
import { communityApi } from '../../api/community'
import { getStorageUrl } from '../../utils/constants'
import Header from '../../components/layout/Header'
import Footer from '../../components/layout/Footer'
import Loading from '../../components/common/Loading'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Card from '../../components/common/Card'
import SEO from '../../components/seo/SEO'
import { generateArticleStructuredData, generateBreadcrumbStructuredData } from '../../utils/seo'
import { FiHeart } from 'react-icons/fi'
import { AiFillHeart } from 'react-icons/ai'
import { FiMessageCircle, FiShare2, FiCornerDownRight } from 'react-icons/fi'
import './CommunityPost.css'

const CommunityPost = () => {
  const { id } = useParams()
  const { isAuthenticated, user } = useAuth()
  const queryClient = useQueryClient()
  const [comment, setComment] = useState('')
  const [replyingTo, setReplyingTo] = useState(null)
  const [replyText, setReplyText] = useState({})

  const { data, isLoading, error } = useQuery({
    queryKey: ['community-post', id],
    queryFn: async () => {
      try {
        const result = await communityApi.getPost(id)
        console.log('Post data fetched:', {
          postId: id,
          commentsCount: result?.data?.comments?.length || 0,
          comments: result?.data?.comments?.map(c => ({
            id: c.id,
            parent_id: c.parent_id,
            content: c.content?.substring(0, 30)
          }))
        })
        return result
      } catch (err) {
        // If 401, the post might require authentication
        // If 404, the post doesn't exist or isn't approved
        throw err
      }
    },
    retry: false,
    enabled: !!id,
    // Allow refetch on mount to get fresh data (including saved replies)
    refetchOnMount: true,
    // Prevent refetch on window focus to avoid overwriting optimistic updates
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const commentMutation = useMutation({
    mutationFn: (data) => communityApi.commentOnPost(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['community-post', id])
      setComment('')
    },
  })

  const replyMutation = useMutation({
    mutationFn: ({ commentId, data }) => {
      // Ensure parent_id is an integer (not string)
      const parentId = parseInt(commentId, 10)
      if (isNaN(parentId)) {
        throw new Error('Invalid parent comment ID')
      }
      const payload = { ...data, parent_id: parentId }
      console.log('Creating reply with payload:', { 
        commentId, 
        parentId,
        payload, 
        postId: id,
        payloadString: JSON.stringify(payload)
      })
      return communityApi.commentOnPost(id, payload)
        .then(response => {
          console.log('Reply API response:', response)
          // Attach commentId to response for use in onSuccess
          return { ...response, _commentId: commentId }
        })
        .catch(error => {
          console.error('Reply creation error:', error)
          console.error('Error response:', error.response)
          throw error
        })
    },
    onSuccess: (response) => {
      console.log('Reply created successfully - full response:', response)
      // Backend returns: { success: true, data: comment }
      // So response.data.data is the comment object
      const newReply = response?.data?.data || response?.data || response
      const parentCommentId = response?._commentId || newReply?.parent_id
      console.log('New reply extracted:', {
        id: newReply.id,
        parent_id: newReply.parent_id,
        content: newReply.content,
        user_id: newReply.user_id,
        user: newReply.user,
        parentCommentId
      })
      
      // Verify the reply was actually created with parent_id
      if (!newReply.id) {
        console.error('Reply missing ID!', newReply)
        alert('حدث خطأ في إنشاء الرد. يرجى المحاولة مرة أخرى.')
        return
      }
      
      if (!newReply.parent_id && parentCommentId) {
        console.warn('Reply missing parent_id, using from mutation:', parentCommentId)
      }
      
      // Ensure the reply has all required fields
      if (!newReply.user && newReply.user_id) {
        // If user relationship is missing, use the current user from context
        newReply.user = user ? {
          id: user.id,
          name: user.name,
          avatar: user.avatar
        } : null
      }
      
      // Ensure parent_id is set correctly
      if (newReply.parent_id == null && parentCommentId) {
        newReply.parent_id = parentCommentId
      }
      
      // Ensure likes_count and is_liked are set
      if (newReply.likes_count === undefined) newReply.likes_count = 0
      if (newReply.is_liked === undefined) newReply.is_liked = false
      
      // Optimistically update the cache with the new reply
      queryClient.setQueryData(['community-post', id], (oldData) => {
        if (!oldData) {
          console.log('No old data found, skipping optimistic update')
          return oldData
        }
        const post = oldData?.data || oldData?.post || oldData
        const currentComments = post.comments || []
        console.log('Current comments count:', currentComments.length)
        
        // Check if reply already exists (avoid duplicates)
        const replyExists = currentComments.some(c => c.id === newReply.id)
        if (!replyExists && newReply.id) {
          // Add the new reply to comments array
          const updatedComments = [...currentComments, newReply]
          console.log('Updated comments count:', updatedComments.length, 'New reply added:', {
            id: newReply.id,
            parent_id: newReply.parent_id,
            content: newReply.content?.substring(0, 50)
          })
          
          return {
            ...oldData,
            data: {
              ...post,
              comments: updatedComments,
              comments_count: updatedComments.length
            }
          }
        } else {
          console.log('Reply already exists or missing ID:', { replyExists, replyId: newReply.id })
        }
        return oldData
      })
      
      // Close the reply form immediately
      setReplyingTo(null)
      setReplyText({})
      
      // Refetch after a delay to ensure the reply is saved and persisted
      // This ensures the reply appears even after page reload
      // Use refetchQueries instead of invalidateQueries to ensure we get fresh data
      setTimeout(async () => {
        console.log('Refetching post data to verify reply was saved...')
        try {
          await queryClient.refetchQueries({
            queryKey: ['community-post', id],
            exact: true
          })
          console.log('Post data refetched successfully')
        } catch (error) {
          console.error('Error refetching post data:', error)
        }
      }, 2000) // 2 second delay to ensure DB transaction is committed
    },
    onError: (error) => {
      console.error('Error creating reply:', error)
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      })
      alert('فشل في إرسال الرد. يرجى المحاولة مرة أخرى.')
      // Don't close the reply form on error so user can retry
    }
  })

  const postLikeMutation = useMutation({
    mutationFn: () => communityApi.reactToPost(id, {}),
    onSuccess: (response) => {
      // Update the post's liked status from API response
      queryClient.setQueryData(['community-post', id], (oldData) => {
        if (!oldData) return oldData
        const post = oldData?.data || oldData?.post || oldData
        const responseData = response?.data || {}
        
        return {
          ...oldData,
          data: {
            ...post,
            is_liked: responseData.is_liked !== undefined ? responseData.is_liked : !post.is_liked,
            likes_count: responseData.like_count !== undefined ? responseData.like_count : (post.likes_count || 0),
          },
        }
      })
    },
  })

  const commentLikeMutation = useMutation({
    mutationFn: ({ commentId }) => communityApi.reactToComment(id, commentId, {}),
    onSuccess: (response, variables) => {
      // Update the comment's liked status from API response
      queryClient.setQueryData(['community-post', id], (oldData) => {
        if (!oldData) return oldData
        const post = oldData?.data || oldData?.post || oldData
        const comments = post?.comments || []
        const responseData = response?.data || {}
        
        const updatedComments = comments.map((comment) => {
          if (comment.id === variables.commentId) {
            return {
              ...comment,
              is_liked: responseData.is_liked !== undefined ? responseData.is_liked : !comment.is_liked,
              likes_count: responseData.like_count !== undefined ? responseData.like_count : (comment.likes_count || 0),
            }
          }
          // Also check replies
          if (comment.replies) {
            comment.replies = comment.replies.map((reply) => {
              if (reply.id === variables.commentId) {
                return {
                  ...reply,
                  is_liked: responseData.is_liked !== undefined ? responseData.is_liked : !reply.is_liked,
                  likes_count: responseData.like_count !== undefined ? responseData.like_count : (reply.likes_count || 0),
                }
              }
              return reply
            })
          }
          return comment
        })
        
        return {
          ...oldData,
          data: {
            ...post,
            comments: updatedComments,
          },
        }
      })
    },
  })

  if (isLoading) {
    return (
      <>
        <Header />
        <Loading fullScreen text="جاري التحميل..." />
        <Footer />
      </>
    )
  }

  if (error) {
    const status = error.response?.status
    const isUnauthorized = status === 401 || status === 403
    const isNotFound = status === 404
    
    return (
      <>
        <Header />
        <div className="error-container">
          {isNotFound ? (
            <>
              <p>المنشور غير موجود</p>
              <p className="error-subtitle">قد يكون المنشور غير معتمد أو تم حذفه</p>
            </>
          ) : isUnauthorized ? (
            <>
              <p>يجب تسجيل الدخول لعرض هذا المنشور</p>
              <p className="error-subtitle">
                <Link to="/login" style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>
                  اضغط هنا لتسجيل الدخول
                </Link>
              </p>
            </>
          ) : (
            <>
              <p>حدث خطأ أثناء تحميل المنشور</p>
              <p className="error-subtitle">يرجى المحاولة مرة أخرى</p>
            </>
          )}
        </div>
        <Footer />
      </>
    )
  }

  // Handle different response structures - API returns post directly in data
  const post = data?.data || data?.post || data
  const siteUrl = 'https://3afer.net'
  const articleStructuredData = post?.id ? generateArticleStructuredData(post, siteUrl) : null
  const breadcrumbData = post?.id ? generateBreadcrumbStructuredData([
    { name: 'الرئيسية', url: '/' },
    { name: 'المجتمع', url: '/community' },
    { name: post.title || 'المنشور', url: `/community/posts/${post.id}` }
  ], siteUrl) : null

  const structuredDataArray = [articleStructuredData, breadcrumbData].filter(Boolean)

  if (!post || !post.id) {
    return (
      <>
        <Header />
        <div className="error-container">
          <p>المنشور غير موجود</p>
        </div>
        <Footer />
      </>
    )
  }

  const comments = post?.comments || []
  // Check if post is liked
  // API returns is_liked (boolean) - see CommunityController line 136
  // Primary: Check is_liked field (strict check for true)
  // Fallback: Check if current user is in likes array
  const isLiked = post?.is_liked === true || 
                 post?.is_liked === 'true' || 
                 post?.is_liked === 1 ||
                 (user && post?.likes && Array.isArray(post.likes) && 
                  post.likes.some(like => {
                    const likeUserId = like.user_id || like.user?.id
                    return likeUserId && likeUserId.toString() === user.id.toString()
                  }))
  const likesCount = post?.likes_count || post?.reactions_count || post?.likes?.length || 0
  
  // Debug logging - check browser console to see what the API returns
  console.log(`Post ${post?.id}:`, {
    is_liked: post?.is_liked,
    is_liked_type: typeof post?.is_liked,
    is_liked_strict_true: post?.is_liked === true,
    likes_count: likesCount,
    likes_array: post?.likes,
    user_id: user?.id,
    calculated_isLiked: isLiked
  })

  const handleComment = (e) => {
    e.preventDefault()
    if (!isAuthenticated) {
      alert('يجب تسجيل الدخول للتعليق')
      return
    }
    if (!comment.trim()) {
      return
    }
    commentMutation.mutate({ content: comment })
  }

  const handleReply = (commentId) => {
    if (!isAuthenticated) {
      alert('يجب تسجيل الدخول للرد')
      return
    }
    const reply = replyText[commentId]?.trim()
    if (!reply) {
      return
    }
    replyMutation.mutate({ commentId, data: { content: reply } })
  }

  const handlePostLike = () => {
    if (!isAuthenticated) {
      alert('يجب تسجيل الدخول للمشاركة')
      return
    }
    postLikeMutation.mutate()
  }

  const handleCommentLike = (commentId) => {
    if (!isAuthenticated) {
      alert('يجب تسجيل الدخول للمشاركة')
      return
    }
    commentLikeMutation.mutate({ commentId })
  }

  // Filter top-level comments (no parent_id) and group replies
  // Handle both null/undefined and ensure parent_id comparison works with string/number
  const topLevelComments = comments.filter(c => !c.parent_id && c.parent_id !== 0)
  const repliesByParent = comments
    .filter(c => c.parent_id != null && c.parent_id !== 0)
    .reduce((acc, reply) => {
      // Convert parent_id to string for consistent key matching
      const parentId = String(reply.parent_id)
      if (!acc[parentId]) {
        acc[parentId] = []
      }
      acc[parentId].push(reply)
      return acc
    }, {})

  // Debug logging to help identify issues
  console.log('Comments data:', {
    totalComments: comments.length,
    topLevelCount: topLevelComments.length,
    repliesCount: comments.filter(c => c.parent_id != null && c.parent_id !== 0).length,
    repliesByParent,
    sampleComment: comments[0],
    sampleReply: comments.find(c => c.parent_id != null)
  })

  return (
    <div className="community-post-page">
      <SEO
        title={post.title}
        description={post.content?.substring(0, 160) || post.title}
        image={post.image ? getStorageUrl(post.image) : undefined}
        url={`/community/posts/${post.id}`}
        type="article"
        structuredData={structuredDataArray.length > 0 ? structuredDataArray : undefined}
      />
      <Header />
      <main className="community-post-main">
        <div className="container">
          <Card className="post-detail">
            <div className="post-header">
              <div className="post-author">
                {post.user?.avatar ? (
                  <img src={getStorageUrl(post.user.avatar)} alt={post.user.name} className="author-avatar" />
                ) : (
                  <div className="author-avatar-placeholder">
                    {post.user?.name?.[0] || 'U'}
                  </div>
                )}
                <div>
                  <h3 className="author-name">{post.user?.name || 'مستخدم'}</h3>
                  <span className="post-date">
                    {new Date(post.created_at).toLocaleDateString('ar-EG')}
                  </span>
                </div>
              </div>
            </div>
            <div className="post-content">
              <p>{post.content}</p>
              {post.image_url && (
                <div className="post-image-container">
                  <img src={getStorageUrl(post.image_url)} alt="Post image" className="post-image" />
                </div>
              )}
            </div>
            <div className="post-footer">
              <button
                className={`post-action ${isLiked ? 'liked' : ''}`}
                onClick={handlePostLike}
                disabled={postLikeMutation.isPending}
                style={isLiked ? { color: '#ef4444' } : {}}
              >
                {isLiked ? (
                  <AiFillHeart style={{ color: '#ef4444', fontSize: '18px' }} />
                ) : (
                  <FiHeart style={{ fontSize: '18px' }} />
                )}
                <span>{likesCount}</span>
              </button>
              <span className="post-action">
                <FiMessageCircle />
                <span>{post.comments_count || comments.length}</span>
              </span>
            </div>
          </Card>

          <div className="comments-section">
            <h2 className="section-title">التعليقات ({comments.length})</h2>
            
            {isAuthenticated && (
              <form onSubmit={handleComment} className="comment-form">
                <Input
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="اكتب تعليقاً..."
                  fullWidth
                />
                <Button type="submit" variant="primary">
                  إرسال
                </Button>
              </form>
            )}

            <div className="comments-list">
              {topLevelComments.map((comment) => {
                // Convert comment.id to string to match the key in repliesByParent
                const replies = repliesByParent[String(comment.id)] || []
                // API returns is_liked (boolean) - see CommunityController line 143
                // Handle both boolean and string values from API
                const isCommentLiked = comment?.is_liked === true || 
                                     comment?.is_liked === 'true' || 
                                     comment?.is_liked === 1
                const commentLikesCount = comment?.likes_count || comment?.reactions_count || 0
                
                return (
                  <div key={comment.id} className="comment-wrapper">
                    <Card className="comment-card">
                  <div className="comment-author">
                    {comment.user?.avatar ? (
                      <img src={getStorageUrl(comment.user.avatar)} alt={comment.user.name} className="comment-avatar" />
                        ) : (
                          <div className="comment-avatar-placeholder">
                            {comment.user?.name?.[0] || 'U'}
                          </div>
                        )}
                        <div className="comment-author-info">
                          <h4 className="comment-author-name">{comment.user?.name || 'مستخدم'}</h4>
                          <span className="comment-date">
                            {new Date(comment.created_at).toLocaleDateString('ar-EG')}
                          </span>
                        </div>
                      </div>
                      <p className="comment-content">{comment.content}</p>
                      <div className="comment-actions">
                        <button
                          className={`comment-action ${isCommentLiked ? 'liked' : ''}`}
                          onClick={() => handleCommentLike(comment.id)}
                          disabled={commentLikeMutation.isPending}
                          style={isCommentLiked ? { color: '#ef4444' } : {}}
                        >
                          {isCommentLiked ? (
                            <AiFillHeart style={{ color: '#ef4444', fontSize: '16px' }} />
                          ) : (
                            <FiHeart style={{ fontSize: '16px' }} />
                          )}
                          <span>{commentLikesCount}</span>
                        </button>
                        {isAuthenticated && (
                          <button
                            className="comment-action"
                            onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                          >
                            <FiMessageCircle />
                            <span>رد</span>
                          </button>
                        )}
                      </div>
                      
                      {replyingTo === comment.id && (
                        <div className="reply-form">
                          <Input
                            value={replyText[comment.id] || ''}
                            onChange={(e) => setReplyText({ ...replyText, [comment.id]: e.target.value })}
                            placeholder={`رد على ${comment.user?.name || 'المستخدم'}...`}
                            fullWidth
                          />
                          <div className="reply-form-actions">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleReply(comment.id)}
                              disabled={replyMutation.isPending}
                            >
                              إرسال
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                setReplyingTo(null)
                                setReplyText({ ...replyText, [comment.id]: '' })
                              }}
                            >
                              إلغاء
                            </Button>
                          </div>
                        </div>
                      )}

                      {replies.length > 0 && (
                        <div className="replies-list">
                          {replies.map((reply) => {
                            // API returns is_liked (boolean) - see CommunityController line 149
                            // Handle both boolean and string values from API
                            const isReplyLiked = reply?.is_liked === true || 
                                               reply?.is_liked === 'true' || 
                                               reply?.is_liked === 1
                            const replyLikesCount = reply?.likes_count || reply?.reactions_count || 0
                            
                            return (
                              <div key={reply.id} className="reply-item">
                                <div className="reply-indicator">
                                  <FiCornerDownRight />
                                </div>
                                <Card className="reply-card">
                                  <div className="comment-author">
                                    {reply.user?.avatar ? (
                                      <img src={getStorageUrl(reply.user.avatar)} alt={reply.user.name} className="comment-avatar" />
                                    ) : (
                                      <div className="comment-avatar-placeholder">
                                        {reply.user?.name?.[0] || 'U'}
                                      </div>
                                    )}
                                    <div className="comment-author-info">
                                      <h4 className="comment-author-name">{reply.user?.name || 'مستخدم'}</h4>
                                      <span className="comment-date">
                                        {new Date(reply.created_at).toLocaleDateString('ar-EG')}
                                      </span>
                                    </div>
                                  </div>
                                  <p className="comment-content">{reply.content}</p>
                                  <div className="comment-actions">
                                    <button
                                      className={`comment-action ${isReplyLiked ? 'liked' : ''}`}
                                      onClick={() => handleCommentLike(reply.id)}
                                      disabled={commentLikeMutation.isPending}
                                      style={isReplyLiked ? { color: '#ef4444' } : {}}
                                    >
                                      {isReplyLiked ? (
                                        <AiFillHeart style={{ color: '#ef4444', fontSize: '16px' }} />
                                      ) : (
                                        <FiHeart style={{ fontSize: '16px' }} />
                                      )}
                                      <span>{replyLikesCount}</span>
                                    </button>
                                  </div>
                                </Card>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </Card>
                  </div>
                )
              })}
              {topLevelComments.length === 0 && (
                <p className="no-comments">لا توجد تعليقات</p>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default CommunityPost

